import styled from '@emotion/styled';
import { Box } from '~Styles';

const fontSizeMap: { [key: string]: string } = {
  medium: '22px',
  large: '28px',
  xl: '36px',
};

const minHeightMap: { [key: string]: string } = {
  medium: '72px',
  large: '96px',
  xl: '128px',
};

/**
 * Large, touch-first button for the Express Kiosk. Same active/disabled/touch
 * animation behaviour as ~Styles/buttons Button, scaled up for kiosk screens.
 * Defaults to the venue's menu colors (set as CSS vars by Wrapper).
 */
export const Button = styled(Box)<{
  disabled?: boolean;
  disabledToast?: string;
  touching?: boolean;
  size?: 'medium' | 'large' | 'xl';
}>`
  outline: none;
  appearance: none;
  border-style: none;
  font-weight: 700;
  min-width: 96px;
  font-family: var(--eposFontFamily);
  color: var(--eposText);
  background-color: var(--eposSurface);
  letter-spacing: 0.24px;
  cursor: pointer;
  border-radius: 16px;
  transition: all 0.2s ease-out;
  &:active {
    filter: brightness(0.92);
  }
  ${({ disabled }) => disabled && 'opacity: .2;'}
  ${({ disabled, disabledToast }) => disabled && !disabledToast && 'pointer-events: none;'}
  transform: ${({ touching }) => (touching ? 'scale(0.96)' : 'none')};

  font-size: ${({ size }) => fontSizeMap[size || 'large']};
  min-height: ${({ size }) => minHeightMap[size || 'large']};
`;

Button.defaultProps = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  as: 'button',
  padding: 'large',
  size: 'large',
};

/** Primary call-to-action button, e.g. "Pay" / "Order" - uses the venue's main theme color. */
export const MainButton = styled(Button)`
  background: var(--eposButton);
  color: var(--eposButtonText);
`;

/** Navigation-colored button, e.g. category tabs - primary background, accent text. */
export const SecondaryButton = styled(Button)`
  background: var(--eposPrimary);
  color: var(--eposAccent);
`;

/** Destructive action, e.g. "Cancel order" - intentionally not theme-driven. */
export const RedButton = styled(Button)`
  background: var(--colorUIRed);
  color: white;
`;

export const OutlinedButton = styled(Button)`
  background: transparent;
  border: solid 2px var(--eposText);
  color: var(--eposText);
`;

export const TransparentButton = styled(Button)`
  background: transparent;
  color: var(--eposText);
  &:active {
    background-color: transparent;
    filter: none;
  }
`;
